import { Component } from 'react';

/**
 * Catches a crash in part of the site so the shopper sees a message and a way on, instead of a blank white screen.
 * App wraps the pages (with the path as `resetKey`: moving to another page clears the message without remounting
 * anything) and the checkout separately, so a checkout crash never takes the whole site down. `fallback` replaces the
 * default full-page message.
 */
export default class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidUpdate(prevProps) {
    if (this.state.error && prevProps.resetKey !== this.props.resetKey) this.setState({ error: null });
  }

  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Page crashed:', error, info?.componentStack);
  }

  render() {
    if (!this.state.error) return this.props.children;
    if (this.props.fallback !== undefined) return this.props.fallback;
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 py-16" role="alert">
        <div className="max-w-md text-center">
          <h1 className="text-2xl font-bold text-gray-900">Something went wrong</h1>
          <p className="mt-3 text-sm text-gray-600">
            This page couldn't be shown. Your cart is saved — please reload, or continue from the home page.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 justify-center">
            <button type="button" onClick={() => window.location.reload()}
              className="px-5 py-2.5 rounded-xl bg-[#5B21B6] text-white text-sm font-semibold">
              Reload page
            </button>
            <a href="/" className="px-5 py-2.5 rounded-xl border border-[#5B21B6] text-[#5B21B6] text-sm font-semibold">
              Home page
            </a>
          </div>
        </div>
      </div>
    );
  }
}

/** Shown in place of the checkout if it crashes: the rest of the site keeps working. */
export const CheckoutCrashed = () => (
  <div className="fixed bottom-4 left-4 right-4 z-[60] mx-auto max-w-md bg-white border border-[#5B21B6] rounded-xl p-4 shadow-lg" role="alert">
    <p className="text-sm text-gray-900 font-semibold">Checkout ran into a problem.</p>
    <p className="text-xs text-gray-600 mt-1">Your cart is saved. Please reload the page and try again.</p>
    <button type="button" onClick={() => window.location.reload()}
      className="mt-3 px-4 py-2 rounded-lg bg-[#5B21B6] text-white text-xs font-semibold">
      Reload page
    </button>
  </div>
);
